/* Iter251 · Sunbiz Enrich Button
   ---------------------------------------------------------------
   Sits inside the "Company" card. Fetches the entity from
   search.sunbiz.org (via /smith/sunbiz/enrich/{pid}) and merges:
     • document_number / fei_ein / entity_type / date_filed
     • principal_address / mailing_address
     • Officers as new decision_makers
     • Cross-ref to OCPA parcel when principal_address matches
*/
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Landmark, Loader2, Link2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export function SunbizEnrichButton({ prospectId, state, onEnriched,
                                        testid = "sunbiz-enrich-btn" }) {
  const [loading, setLoading] = useState(false);
  const canEnrich = state === "FL";

  const run = async () => {
    if (!canEnrich) {
      toast.error("Sunbiz cobre apenas Flórida — este lead não é elegível");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/smith/sunbiz/enrich/${prospectId}`, {},
        { timeout: 40000 });
      if (data.ok) {
        const fieldsN = Object.keys(data.fields_added || {}).length;
        const officersN = (data.officers_added || []).length;
        const parts = [];
        if (fieldsN) parts.push(`${fieldsN} campos`);
        if (officersN) parts.push(`${officersN} officers`);
        if (data.ocpa_cross_reference) parts.push("↔ OCPA");
        toast.success(`Sunbiz OK · ${parts.join(" · ") || "empresa já atualizada"}`);
        onEnriched?.(data);
      } else if (data.status === "cache_hit") {
        toast.info("Sunbiz respondeu via cache");
        onEnriched?.(data);
      } else if (data.status === "not_found") {
        toast.error("Empresa não encontrada no Sunbiz");
      } else if (data.status === "blocked" || data.status === "timeout") {
        // Iter255 · Sunbiz refused our datacenter IP (403) or the
        // upstream took too long — open the pre-filled search URL in
        // a new tab so the operator can proceed manually.
        const label = data.status === "blocked"
          ? "Sunbiz bloqueou nosso IP — abrindo Sunbiz em nova aba"
          : "Sunbiz demorou demais — abrindo em nova aba";
        toast.info(label, {
          duration: 8000,
          action: {
            label: "Abrir de novo",
            onClick: () => window.open(data.direct_search_url, "_blank", "noopener"),
          },
        });
        if (data.direct_search_url) {
          window.open(data.direct_search_url, "_blank", "noopener");
        }
      } else {
        toast.error(`Sunbiz falhou: ${data.status}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao chamar Sunbiz");
    } finally { setLoading(false); }
  };

  return (
    <button type="button" onClick={run} disabled={loading || !canEnrich}
            data-testid={testid}
            title={canEnrich ? "Enriquecer via Florida Sunbiz (busca corporativa)"
                                : "Sunbiz cobre apenas Flórida"}
            className="h-8 px-2.5 rounded-lg font-bold text-[11px] text-white inline-flex items-center gap-1 disabled:opacity-40"
            style={{ background: "linear-gradient(135deg,#F97316 0%,#B45309 100%)" }}>
      {loading
        ? <><Loader2 className="w-3 h-3 animate-spin" /> Sunbiz…</>
        : <><Landmark className="w-3 h-3" /> Enrich Sunbiz</>}
    </button>
  );
}


export function SunbizCrossRefBadge({ hasParcelId, sunbizDocNumber, sunbizDetailUrl }) {
  /* Iter251 · Small chip shown at the top of the Company card when the
     lead has both a Sunbiz doc# AND an OCPA parcel — makes the
     "company owns this property" link visible at a glance. */
  if (!hasParcelId || !sunbizDocNumber) return null;
  return (
    <div data-testid="sunbiz-ocpa-crossref"
         className="mt-1 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/25 text-emerald-200 font-bold uppercase tracking-wider">
      <Link2 className="w-3 h-3" /> Sunbiz ↔ OCPA
      {sunbizDetailUrl && (
        <a href={sunbizDetailUrl} target="_blank" rel="noopener noreferrer"
           className="underline hover:text-emerald-100 ml-1"
           data-testid="sunbiz-detail-link">
          ver
        </a>
      )}
    </div>
  );
}
